<script setup>
import {ref, computed, watch, defineProps, defineEmits, getCurrentInstance} from 'vue'
import { validateInvoice } from '../validation/invoice.validator'
import { INVOICE_TYPE_ABBREVIATIONS } from '../constants'

import {
	DiscountType,
	InvoiceStatus
} from '../types.js'

import {
	INITIAL_INVOICE,
	AVAILABLE_TAXES
} from '../constants.js'

import InvoiceTypeSelect from './InvoiceTypeSelect.vue'
import RecipientForm from './RecipientForm.vue'
import ItemRow from './ItemRow.vue'
import JsonModal from './JsonModal.vue'

import {
	CopyIcon,
	ClipboardPasteIcon,
	SaveIcon,
	XIcon,
	CalendarIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	PlusIcon,
	CheckIcon
} from './Icons.js'
import { notify } from '@/utils/notify'

const instance = getCurrentInstance()
const t = (key) => instance?.ctx.$i18n.t(key)

// ------------------------------
// PROPS
// ------------------------------
const props = defineProps({
	initialInvoice: { type: Object, default: null },
})

// ------------------------------
// EMITS
// ------------------------------
const emit = defineEmits(['setLang', 'save', 'cancel'])

// ------------------------------
// STATE
// ------------------------------
const invoice = ref(
	JSON.parse(JSON.stringify(props.initialInvoice || INITIAL_INVOICE))
)

const isJsonModalOpen = ref(false)
const isSenderExpanded = ref(false)
const isNoteVisible = ref(!!(props.initialInvoice?.note || INITIAL_INVOICE.note))

const today = new Date().toISOString().slice(0, 10)

// ------------------------------
// WATCH invoice changes
// ------------------------------
watch(
	() => props.initialInvoice,
	(newVal) => {
		if (newVal) {
			invoice.value = JSON.parse(JSON.stringify(newVal))
			isNoteVisible.value = !!newVal.note
		} else {
			invoice.value = JSON.parse(JSON.stringify(INITIAL_INVOICE))
		}
	}
)

// ------------------------------
// COMPUTED TOTALS
// ------------------------------
const lines = computed(() =>
	invoice.value.items.map(item => {
		const grossCents = item.quantity * item.price

		let discountCents = 0
		if (item.discount?.value > 0) {
			discountCents =
				item.discount.type === DiscountType.PERCENTAGE
					? Math.round(grossCents * item.discount.value / 100)
					: Math.round(item.discount.value * 100)
		}

		const netCents = Math.max(0, grossCents - discountCents)

		return {
			...item,
			grossCents,
			discountCents,
			netCents,
		}
	})
)

const subtotal = computed(() =>
	lines.value.reduce((s, l) => s + l.netCents, 0)
)

const isCreateMode = computed(() => !props.initialInvoice)

const taxBreakdown = computed(() => {
	const bd = {}
	lines.value.forEach(l => {
		l.taxes.forEach(t => {
			const amount = Math.round(l.netCents * t.rate / 100)
			bd[t.id] ??= { name: t.name, rate: t.rate, amount: 0 }
			bd[t.id].amount += amount
		})
	})
	return bd
})

const finalTotal = computed(() =>
	subtotal.value +
	Object.values(taxBreakdown.value).reduce((s, t) => s + t.amount, 0)
)

// ------------------------------
// ITEM HANDLERS
// ------------------------------
const handleAddItem = () => {
	const newItem = {
		id: Math.random().toString(36).substr(2, 9),
		name: '',
		code: '',
		quantity: 1,
		unit: 'pcs',
		price: 0,
		taxes: [AVAILABLE_TAXES[0]],
		discount: { type: DiscountType.PERCENTAGE, value: 0, comment: '' },
	}

	invoice.value.items.push(newItem)
}

const handleRemoveItem = (id) => {
	if (invoice.value.items.length <= 1) return
	invoice.value.items = invoice.value.items.filter((i) => i.id !== id)
}

const handleUpdateItem = (id, field, value) => {
	const item = invoice.value.items.find((i) => i.id === id)
	if (item) item[field] = value
}

const handleDuplicateItem = (id) => {
	const idx = invoice.value.items.findIndex((i) => i.id === id)
	if (idx === -1) return

	const original = invoice.value.items[idx]
	const copy = JSON.parse(JSON.stringify(original))
	copy.id = Math.random().toString(36).substr(2, 9)

	invoice.value.items.splice(idx + 1, 0, copy)
}

// ------------------------------
// ACTION HANDLERS
// ------------------------------
const handleSaveDraft = () => {
	const errors = validateInvoice(invoice.value, t)

	if (errors.length) {
		notify.error(errors[0])
		return
	}

	emit('save', { ...invoice.value, status: InvoiceStatus.DRAFT })
}

const handleActivate = () => {
	const errors = validateInvoice(invoice.value, t)

	if (errors.length) {
		notify.error(errors[0])
		return
	}

	emit('save', { ...invoice.value, status: InvoiceStatus.ACTIVATED })
}

// ------------------------------
// JSON COPY & PASTE
// ------------------------------
const copyToClipboard = () => {
	navigator.clipboard.writeText(JSON.stringify(invoice.value, null, 2))
	notify.success(t('invoices.json.copy'))
}

const handlePasteJson = (jsonStr) => {
	try {
		const parsed = JSON.parse(jsonStr)
		invoice.value = { ...invoice.value, ...parsed }
	} catch {
		notify.error(t('invoices.json.error'))
	}
}

// ------------------------------
// GRID CLASS
// ------------------------------
const gridLayoutClass =
	'grid grid-cols-[1fr_70px_60px_70px_100px_140px_150px_110px] gap-2 items-center'
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div class="bg-white w-full h-full md:h-[95vh] md:w-[95vw] md:max-w-[1600px] md:rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">

          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b border-slate-200 bg-white shrink-0">
             <div class="flex items-center gap-4">
                 <h2 class="font-bold text-lg text-slate-900">{{ initialInvoice ? $t('invoices.action_edit') : $t('invoices.new_invoice') }}</h2>
                 <div class="h-6 w-px bg-slate-200"></div>
             </div>

             <div class="flex items-center gap-3">
                 <button @click="copyToClipboard" class="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-xs font-medium hover:bg-slate-50">
                     <CopyIcon class="w-3.5 h-3.5" /> {{ $t('invoices.copy_json') }}
                 </button>
								 <button
									 v-if="isCreateMode"
									 @click="isJsonModalOpen = true"
									 class="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-md text-xs font-medium hover:bg-slate-200"
								 >
									 <ClipboardPasteIcon class="w-3.5 h-3.5" />
									 {{ $t('invoices.paste_json') }}
								 </button>


							 <div class="h-6 w-px bg-slate-200 mx-1 hidden md:block"></div>

                 <button @click="emit('cancel')" class="px-4 py-1.5 border border-slate-200 bg-white rounded-md text-slate-600 font-medium hover:bg-slate-50">{{ $t('invoices.cancel') }}</button>
                 <button @click="handleSaveDraft" class="flex items-center gap-2 px-4 py-1.5 border border-slate-300 bg-white rounded-md text-slate-700 font-medium hover:bg-slate-50"><SaveIcon class="w-3.5 h-3.5" /> {{ $t('invoices.save_draft') }}</button>
                 <button @click="handleActivate" class="px-6 py-1.5 rounded-md font-bold transition-all shadow-sm text-sm bg-blue-600 text-white hover:bg-blue-500">{{ $t('invoices.issue') }}</button>
             </div>
          </div>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto bg-slate-50/50 p-4 md:p-8">
              <div class="max-w-7xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                   <!-- Top Section -->
                   <div class="p-8 border-b border-slate-100 bg-slate-50/30">
                       <div class="mb-8" v-if="invoice.senderName || invoice.senderDetails">
                           <div class="flex items-center gap-2 mb-2">
                               <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider">{{ $t('invoices.sender') }}</h3>
                               <button @click="isSenderExpanded = !isSenderExpanded" class="text-slate-400 hover:text-blue-600">
                                   <component :is="isSenderExpanded ? ChevronUpIcon : ChevronDownIcon" class="w-3.5 h-3.5" />
                               </button>
                           </div>
                           <div class="font-bold text-xl text-slate-900">{{ invoice.senderName }}</div>
                           <div v-if="isSenderExpanded" class="mt-6 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{{ invoice.senderDetails }}</div>
                       </div>

                       <div class="mb-6 pb-6 border-b border-slate-200/60">
                           <InvoiceTypeSelect :value="invoice.type" @change="(type) => invoice.type = type" />
                       </div>

                       <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 bg-white p-6 rounded-lg border border-slate-200">
                           <div class="space-y-1">
                               <label class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.invoice_number') }}</label>
                               <input v-model="invoice.number" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                           </div>
                           <div class="space-y-1 relative">
                               <label class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.date_created') }}</label>
                               <div class="relative">
                                   <input type="date" v-model="invoice.date" :min="today" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" />
                               </div>
                           </div>
                           <div class="space-y-1 relative">
                               <label class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.valid_until') }}</label>
                               <div class="relative">
                                   <input type="date" v-model="invoice.dueDate" :min="invoice.date" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" />
                               </div>
                           </div>
                           <div class="space-y-1">
                               <label class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.currency') }}</label>
                               <div class="relative">
                                   <select v-model="invoice.currency" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none">
                                       <option value="UAH">UAH</option>
                                       <option value="USD">USD</option>
                                       <option value="EUR">EUR</option>
                                   </select>
                                   <ChevronDownIcon class="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" />
                               </div>
                           </div>
                       </div>
                   </div>

                   <!-- Recipient -->
                   <div class="p-8">
                       <RecipientForm
												 :recipient="invoice.recipient"
												 @update:recipient="(r) => invoice.recipient = r" />
                   </div>

                   <!-- Items -->
                   <div class="border-t border-slate-200">
                        <div :class="`bg-slate-50/50 border-y border-slate-200 px-8 py-3 ${gridLayoutClass} text-xs font-medium text-slate-500 uppercase tracking-wider`">
                             <div>{{ $t('invoices.items') }}</div>
                             <div class="text-center">{{ $t('invoices.code') }}</div>
                             <div class="text-center">{{ $t('invoices.qty') }}</div>
                             <div class="text-center">{{ $t('invoices.unit') }}</div>
                             <div class="text-right">{{ $t('invoices.price') }}</div>
                             <div class="text-center text-red-400">{{ $t('invoices.discount_col') }}</div>
                             <div class="text-center">{{ $t('invoices.vat_col') }}</div>
                             <div class="text-right pr-8">{{ $t('invoices.sum') }}</div>
                        </div>
                        <div class="divide-y divide-slate-100">
                             <ItemRow
                                v-for="item in invoice.items"
                                :key="item.id"
                                :item="item"
                                :currency="invoice.currency"
                                :canRemove="invoice.items.length > 1"
                                :gridClassName="gridLayoutClass"
                                @update:item="handleUpdateItem"
                                @remove="handleRemoveItem"
                                @duplicate="handleDuplicateItem"
                             />
                        </div>
                        <div class="px-8 py-4 bg-slate-50/30 border-t border-slate-100">
                           <button @click="handleAddItem" class="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-2 rounded-md transition-all">
                              <div class="w-5 h-5 rounded-full border border-blue-600 flex items-center justify-center"><PlusIcon class="w-3 h-3" /></div>
                              {{ $t('invoices.add_item') }}
                           </button>
                        </div>
                   </div>

                   <!-- Footer -->
                   <div class="p-8 border-t border-slate-200">
                       <div class="flex flex-col lg:flex-row gap-12 items-start h-full">
                           <div class="flex-1 space-y-8 flex flex-col w-full">
                               <div class="space-y-4">
                                   <div class="space-y-2">
                                       <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">{{ $t('invoices.payment_purpose') }}</label>
                                       <textarea v-model="invoice.paymentPurpose" class="w-full h-20 rounded-md border border-slate-200 bg-white p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="..." />
                                   </div>
                                   <div class="flex flex-wrap gap-6">
                                       <label class="flex items-center gap-2 cursor-pointer group select-none">
                                            <div :class="`w-5 h-5 rounded border flex items-center justify-center transition-colors ${invoice.partialPayment ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300 group-hover:border-slate-400'}`">
                                                <CheckIcon v-if="invoice.partialPayment" class="w-3.5 h-3.5 text-white" />
                                            </div>
                                            <span class="text-sm font-medium text-slate-700">{{ $t('invoices.partial_payment') }}</span>
                                            <input type="checkbox" class="hidden" v-model="invoice.partialPayment" />
                                       </label>
                                   </div>
                               </div>
                           </div>
                           <div class="w-full lg:w-[400px] flex flex-col h-full">
                               <div class="space-y-3 pt-2">
                                   <div class="flex justify-between text-sm">
                                       <span class="font-medium text-slate-500">{{ $t('invoices.total_due') }}</span>
                                       <span class="font-bold text-slate-900">{{ (subtotal / 100).toLocaleString('en-US', { minimumFractionDigits: 2 }) }} {{ invoice.currency }}</span>
                                   </div>
                                   <div v-for="(tax, id) in taxBreakdown" :key="id" class="flex justify-between text-sm">
                                       <span class="font-medium text-slate-500">{{ $t('invoices.' + tax.name) }} ({{ tax.rate }}%)</span>
                                       <span class="font-bold text-slate-900">{{ (tax.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2 }) }} {{ invoice.currency }}</span>
                                   </div>
                                   <div class="border-b border-slate-200 my-2"></div>
                                   <div class="flex justify-between items-baseline">
                                       <span class="text-lg font-bold text-slate-900 uppercase">{{ $t('invoices.total') }}</span>
                                       <span class="text-2xl font-black text-slate-900">{{ (finalTotal / 100).toLocaleString('en-US', { minimumFractionDigits: 2 }) }} {{ invoice.currency }}</span>
                                   </div>
                               </div>
                           </div>
                       </div>
                   </div>

              </div>
          </div>
      </div>
      <JsonModal :isOpen="isJsonModalOpen" @close="isJsonModalOpen = false" @save="handlePasteJson" />
			<wc-toast></wc-toast>
  </div>
</template>
